import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { nlBE } from 'date-fns/locale';
import { IconComponent } from './icon.component';

/** Monday-first, matching the club's week and the old picker's firstDayOfWeek: 1. */
const WEEK_OPTIONS = { weekStartsOn: 1 as const, locale: nlBE };

interface Cell {
  date: Date;
  iso: string;
  label: string;
  inMonth: boolean;
  disabled: boolean;
  selected: boolean;
  today: boolean;
}

/**
 * Inline month grid. Replaces p-datepicker.
 *
 * A real <table role="grid"> so the browser gives the semantics for free, and so the e2e
 * suite can address cells by data-date rather than by rendered text ("1" also matches the
 * 1st of a month that minDate has disabled).
 */
@Component({
  selector: 'app-calendar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  template: `
    <div class="max-w-sm rounded-card bg-surface-card p-4">
      <div class="flex items-center justify-between">
        <button
          type="button"
          class="inline-flex size-9 items-center justify-center rounded-md text-fg-muted hover:bg-surface-raised hover:text-fg pointer-coarse:size-11"
          aria-label="Vorige maand"
          data-testid="calendar-prev"
          (click)="shift(-1)"
        >
          <app-icon name="arrow-left" size="size-4" />
        </button>
        <span
          class="text-sm font-semibold text-fg"
          data-testid="calendar-month"
        >
          {{ monthLabel() }}
        </span>
        <button
          type="button"
          class="inline-flex size-9 items-center justify-center rounded-md text-fg-muted hover:bg-surface-raised hover:text-fg pointer-coarse:size-11"
          aria-label="Volgende maand"
          data-testid="calendar-next"
          (click)="shift(1)"
        >
          <app-icon name="arrow-right" size="size-4" />
        </button>
      </div>

      <table
        role="grid"
        class="mt-3 w-full border-collapse"
        data-testid="calendar-grid"
      >
        <thead>
          <tr>
            @for (day of weekdays; track day) {
              <th scope="col" class="pb-2 text-xs font-medium text-fg-subtle">
                {{ day }}
              </th>
            }
          </tr>
        </thead>
        <tbody>
          @for (week of weeks(); track week[0].date.getTime()) {
            <tr>
              @for (cell of week; track cell.date.getTime()) {
                <td class="p-0.5 text-center">
                  <button
                    type="button"
                    [attr.data-date]="cell.iso"
                    [attr.aria-current]="cell.today ? 'date' : null"
                    [attr.aria-selected]="cell.selected"
                    [disabled]="cell.disabled"
                    [class]="cellClass(cell)"
                    (click)="pick(cell)"
                  >
                    {{ cell.label }}
                  </button>
                </td>
              }
            </tr>
          }
        </tbody>
      </table>
    </div>
  `,
})
export class CalendarComponent {
  /** Days the member may pick. Anything not listed renders disabled. */
  readonly selectableDates = input<Date[]>([]);
  /** When true every day from minDate on is selectable, ignoring selectableDates. */
  readonly allowAnyDate = input(false);
  readonly minDate = input<Date>(new Date());
  readonly selected = input<Date | undefined>(undefined);
  readonly datePicked = output<Date>();

  protected readonly weekdays = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
  private readonly cursor = signal(startOfMonth(new Date()));

  protected readonly monthLabel = computed(() =>
    format(this.cursor(), 'LLLL yyyy', { locale: nlBE }),
  );

  protected readonly weeks = computed<Cell[][]>(() => {
    const month = this.cursor();
    const days = eachDayOfInterval({
      start: startOfWeek(startOfMonth(month), WEEK_OPTIONS),
      end: endOfWeek(endOfMonth(month), WEEK_OPTIONS),
    });
    const selectable = this.selectableDates().map((d) => startOfDay(d));
    const min = startOfDay(this.minDate());
    const today = startOfDay(new Date());
    const chosen = this.selected();

    const cells = days.map<Cell>((date) => ({
      date,
      iso: format(date, 'yyyy-MM-dd'),
      label: format(date, 'd'),
      inMonth: isSameMonth(date, month),
      disabled:
        isBefore(date, min) ||
        (!this.allowAnyDate() && !selectable.some((d) => isSameDay(d, date))),
      selected: !!chosen && isSameDay(chosen, date),
      today: isSameDay(date, today),
    }));

    return Array.from({ length: cells.length / 7 }, (_, i) =>
      cells.slice(i * 7, i * 7 + 7),
    );
  });

  protected shift(months: number) {
    this.cursor.update((c) => addMonths(c, months));
  }

  protected pick(cell: Cell) {
    if (!cell.disabled) {
      this.datePicked.emit(cell.date);
    }
  }

  protected cellClass(cell: Cell): string {
    // 44px on a finger, 36px on a mouse: seven 44px cells still fit inside max-w-sm.
    const base =
      'size-9 pointer-coarse:size-11 rounded-md text-sm transition-colors ' +
      'disabled:cursor-not-allowed';
    // Disabled wins over selected: a day the member cannot pick must never look chosen.
    // The padding days of the neighbouring months fade further, so the current month
    // reads as a block without needing a border around it.
    if (cell.disabled) {
      return `${base} ${cell.inMonth ? 'text-fg-subtle' : 'text-fg-subtle/50'}`;
    }
    if (cell.selected) {
      return `${base} bg-brand font-semibold text-brand-fg`;
    }
    // A bookable day gets a visible affordance. Without it the grid reads as broken
    // rather than as "these two days are open".
    return `${base} bg-brand/15 font-semibold text-brand inset-ring inset-ring-brand/40 hover:bg-brand/25`;
  }
}
